import React, { FC, ReactElement } from 'react'
import ButtonGradientEpic from '@/components/button-gradient-epic'
import Button from '@/components/button'
import useWallet from '@/utils/hooks/useWallet'
import { spaceRune } from '@/utils/format-rune'
import event from '@/utils/event'

import Wallet from '@/utils/wallet'
import ToggleSwitch from '@/components/toggle-switch'
import Input from '@/components/input'

function getFirstCharacter(str: any) {
	if (!str || str.length === 0) {
		return ''
	}

	// Check if the first character is a high surrogate
	if (str.charCodeAt(0) >= 0xd800 && str.charCodeAt(0) <= 0xdbff) {
		// If the string has a length of 1, return the lone high surrogate
		if (str.length === 1) {
			return str
		}

		// Return the surrogate pair
		return str.substring(0, 2)
	}

	// Return the first character if it's not a high surrogate
	return str[0]
}

const RuneList: FC<{
	onComplete: () => void
}> = (props): ReactElement => {
	const { onComplete } = props
	// useWallet() kept for its subscription side-effects; data/isLoading unused.
	useWallet()
	const [openEtching, setOpenEtching] = React.useState(true)
	const [loading, setLoading] = React.useState(false)

	const [name, setName] = React.useState('')
	const [supply, setSupply] = React.useState('')
	const [symbol, setSymbol] = React.useState('')
	const [divisibility, setDivisibility] = React.useState('')
	const [limit, setLimit] = React.useState('')
	const [term, setTerm] = React.useState('')
	const [error, setError] = React.useState<any>(null)

	const handleChangeName = React.useCallback((e: any) => setName(spaceRune(e.target.value)), [])
	const handleChangeSupply = React.useCallback((e: any) => setSupply(e.target.value), [])
	const handleChangeSymbol = React.useCallback(
		(e: any) => setSymbol(getFirstCharacter(e.target.value)),
		[]
	)
	const handleChangeDivisibility = React.useCallback((e: any) => setDivisibility(e.target.value), [])
	const handleChangeLimit = React.useCallback((e: any) => setLimit(e.target.value), [])
	const handleChangeTerm = React.useCallback((e: any) => setTerm(e.target.value), [])
	const handleChangeOpenEtching = React.useCallback((v: any) => setOpenEtching(v), [])

	const handleSubmit = React.useCallback(
		async (evt: any) => {
			evt.preventDefault()
			evt.stopPropagation()

			setError(null)
			setLoading(true)

			try {
				const payload = {
					rune: name,
					supply: openEtching ? parseInt(limit) : parseInt(supply),
					symbol,
					divisibility: parseInt(divisibility),
					limit: openEtching ? parseInt(limit) : null,
					//deadline: unde,
					term: openEtching ? parseInt(term) : null
				}

				const { psbt } = await Wallet.getRuneEtch(payload, 1.0)
				const rawtx = await Wallet.signPSBTExtractTx(psbt)
				event.emit('open-broadcast-modal', {
					type: 'transfer',
					inscriptions: [],
					address: Wallet.getTaprootAddress(),
					rawtx
				})

				onComplete()
			} catch (e) {
				setError((e as any).message || e)
				console.log(e)
			}

			setLoading(false)
		},
		[name, supply, symbol, divisibility, limit, term, openEtching, onComplete]
	)

	return (
		<>
			<p>Deploy a new rune token and etch it on chain</p>
			<form className="flex flex-col gap-4" onSubmit={handleSubmit}>
				<div className="flex flex-col gap-1">
					<p className="text-sm">Name</p>
					<Input
						className="border border-border rounded-full h-10"
						inputClassName="text-sm rounded-full"
						value={name}
						onChange={handleChangeName}
					/>
				</div>
				{!openEtching && (
					<div className="flex flex-col gap-1">
						<p className="text-sm">Supply</p>
						<Input
							className="border border-border rounded-full h-10"
							inputClassName="text-sm rounded-full"
							type="number"
							min="1"
							value={supply}
							onChange={handleChangeSupply}
						/>
					</div>
				)}
				<div className="grid grid-cols-2 gap-4">
					<div className="flex flex-col gap-1">
						<p className="text-sm">Symbol</p>
						<Input
							className="border border-border rounded-full h-10"
							inputClassName="text-sm rounded-full"
							value={symbol}
							onChange={handleChangeSymbol}
						/>
					</div>
					<div className="flex flex-col gap-1">
						<p className="text-sm">Divisibility</p>
						<Input
							className="w-full border border-border rounded-full h-10"
							inputClassName="text-sm rounded-full"
							type="number"
							min="1"
							max="255"
							value={divisibility}
							onChange={handleChangeDivisibility}
						/>
					</div>
				</div>
				<div className="flex gap-4 items-center">
					<ToggleSwitch checked={openEtching} onChange={handleChangeOpenEtching} />{' '}
					<p>Open Etching</p>
				</div>
				{openEtching && (
					<>
						<div className="flex flex-col gap-1">
							<p className="text-sm">Mint Limit</p>
							<Input
								className="border border-border rounded-full h-10"
								inputClassName="text-sm rounded-full"
								type="number"
								value={limit}
								onChange={handleChangeLimit}
							/>
						</div>
						<div className="flex flex-col gap-1">
							<p className="text-sm">Mint Block Length</p>
							<Input
								className="border border-border rounded-full h-10"
								inputClassName="text-sm rounded-full"
								type="number"
								value={term}
								onChange={handleChangeTerm}
							/>
						</div>
					</>
				)}
				{error && <p className="mt-4 text-red-500 text-xs">{error}</p>}
				<ButtonGradientEpic
					loading={loading}
					disabled={loading}
					type="submit"
					className="rounded-full"
				>
					Etch Now
				</ButtonGradientEpic>
			</form>
			<Button
				className="text-muted-foreground rounded-full text-base font-semibold p-0 h-12"
				onClick={onComplete}
			>
				Close
			</Button>
		</>
	)
}

export default RuneList
